import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabaseClient } from "@/lib/supabase";
import { useAuth } from "@/utils/auth";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { Badge } from "@patient-watch/ui/badge";
import { Button } from "@patient-watch/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { ClipboardEdit, Copy, Globe, Loader2, MoreVertical, Plus, Trash } from "lucide-react";
import UnauthorizedPage from "@/components/pages/UnauthorizedPage";
import { useSelectForms } from "@/features/forms/api/selectForms";
import { useSelectSurgeryOptions } from "@/features/events/features/surgeries/api/selectSurgeryOptions";
import { useDuplicateTemplate } from "@/features/templates/api/duplicateTemplate";
import { useDeleteTemplate } from "@/features/templates/api/deleteTemplate";
import type { TemplateT } from "@patient-watch/types";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { DraftTemplateWithAiButton } from "@/features/ai-builder/DraftTemplateWithAiButton";

const TemplatesBuilder = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const { mutate: duplicateTemplate } = useDuplicateTemplate();
    const { mutate: deleteTemplate } = useDeleteTemplate();
    const { data: forms } = useSelectForms();
    const { data: surgeryOptions = [] } = useSelectSurgeryOptions();

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Templates", route: { to: "/builder/templates" } },
    ]);

    const { data: templates, isLoading } = useQuery({
        queryKey: ["all-templates-admin"],
        queryFn: async () => {
            const { data, error } = await supabaseClient
                .from("templates")
                .select("*")
                .order("template_name", { ascending: true });

            if (error) throw error;
            return data as TemplateT[];
        },
    });

    if (!auth?.roles?.isAdmin && !auth?.isSuperUser) {
        return <UnauthorizedPage message="Admin Only Page" />;
    }

    const getFormNames = (template: TemplateT) => {
        const settings = template.settings as { trackers?: { form_id: string }[] };
        if (!settings?.trackers || !forms) return [];
        return settings.trackers
            .map((t) => forms.find((f) => f.form_id === t.form_id)?.form_name)
            .filter(Boolean) as string[];
    };

    const getSettingsName = (template: TemplateT) => {
        const s = template.settings as { name?: string };
        return s?.name || template.template_name || "Unnamed";
    };

    const getEventDetails = (template: TemplateT): { event: string; detail: string }[] => {
        const s = template.settings as {
            required_events?: string[];
            injection_details?: Array<{
                injection_substance?: string | null;
                dose?: string | null;
                unit?: string | null;
                site?: string | null;
                orthopaedic_area?: string | null;
            }>;
            shockwave_details?: Array<{
                shockwave_type?: string | null;
                dose?: string | null;
                unit?: string | null;
                impulses?: string | null;
            }>;
            surgery_option_id?: string | null;
            education_name?: string | null;
        };
        const events = s?.required_events || [];
        return events.map((event) => {
            switch (event) {
                case "injection": {
                    const details = s.injection_details || [];
                    if (details.length === 0) return { event: "Injection", detail: "—" };
                    const parts = details.map((d) => {
                        const substance = d.injection_substance || "—";
                        const doseUnit = d.dose && d.unit ? ` (${d.dose} ${d.unit})` : "";
                        return `${substance}${doseUnit}`;
                    });
                    return { event: "Injection", detail: parts.join(", ") };
                }
                case "shockwave": {
                    const details = s.shockwave_details || [];
                    if (details.length === 0) return { event: "Shockwave", detail: "—" };
                    const parts = details.map((d) => {
                        const type = d.shockwave_type || "—";
                        const dose = d.dose && d.unit ? `${d.dose} ${d.unit}` : "";
                        const impulses = d.impulses ? `${d.impulses} impulses` : "";
                        return [type, dose, impulses].filter(Boolean).join(", ");
                    });
                    return { event: "Shockwave", detail: parts.join("; ") };
                }
                case "surgery": {
                    const optId = s.surgery_option_id;
                    const surgery = optId ? surgeryOptions.find((o) => o.surgery_option_id === optId) : null;
                    return {
                        event: "Surgery",
                        detail: surgery?.surgery_name ?? "—",
                    };
                }
                case "education":
                    return {
                        event: "Consultation",
                        detail: s.education_name || "—",
                    };
                default:
                    return { event, detail: "" };
            }
        });
    };

    const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);

    const columns: ColumnDef<TemplateT>[] = useMemo(
        () => [
            {
                id: "name",
                accessorFn: (row) => getSettingsName(row),
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Template Name
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
                cell: ({ row }) => (
                    <div className="space-y-1">
                        <div className="font-medium">{getSettingsName(row.original)}</div>
                        {row.original.template_description && (
                            <div className="text-muted-foreground text-xs line-clamp-1">
                                {row.original.template_description}
                            </div>
                        )}
                    </div>
                ),
            },
            {
                accessorKey: "is_public",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Visibility
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
                cell: ({ row }) => (
                    <Badge variant={row.original.is_public ? "default" : "secondary"} className="text-xs">
                        {row.original.is_public ? "Published" : "Draft"}
                    </Badge>
                ),
            },
            {
                id: "forms",
                accessorFn: (row) => getFormNames(row).join(", "),
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Forms
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
                cell: ({ row }) => (
                    <div className="flex flex-wrap gap-1 min-w-[240px] max-w-[320px]">
                        {getFormNames(row.original).length ? (
                            getFormNames(row.original).map((name) => (
                                <Badge key={name} variant="outline" className="text-xs">
                                    {name}
                                </Badge>
                            ))
                        ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                        )}
                    </div>
                ),
            },
            {
                id: "specialties",
                accessorFn: (row) => row.specialties?.join(", ") ?? "",
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Specialties
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
                cell: ({ row }) => (
                    <div className="flex flex-wrap gap-1 min-w-[240px] max-w-[320px]">
                        {row.original.specialties?.length ? (
                            <>
                                {row.original.specialties.slice(0, 2).map((s) => (
                                    <Badge key={s} variant="secondary" className="text-xs">
                                        {s}
                                    </Badge>
                                ))}
                                {row.original.specialties.length > 2 && (
                                    <Badge variant="outline" className="text-xs">
                                        +{row.original.specialties.length - 2}
                                    </Badge>
                                )}
                            </>
                        ) : (
                            <span className="text-muted-foreground text-sm">None</span>
                        )}
                    </div>
                ),
            },
            {
                id: "events",
                accessorFn: (row) =>
                    getEventDetails(row)
                        .map(({ event, detail }) => `${event}: ${detail}`)
                        .join("; "),
                header: ({ column }) => (
                    <button
                        type="button"
                        className="flex items-center gap-1 font-medium hover:text-foreground"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        Events
                        {{
                            asc: " ↑",
                            desc: " ↓",
                        }[column.getIsSorted() as string] ?? " ↕"}
                    </button>
                ),
                cell: ({ row }) => {
                    const eventDetails = getEventDetails(row.original);
                    return (
                        <div className="flex flex-wrap gap-1 min-w-[200px] max-w-[360px]">
                            {eventDetails.length ? (
                                eventDetails.map(({ event, detail }, i) => (
                                    <Badge
                                        key={`${event}-${i}`}
                                        variant="outline"
                                        className="font-normal text-xs"
                                        title={`${event}: ${detail}`}
                                    >
                                        <span className="font-medium">{event}:</span> {detail}
                                    </Badge>
                                ))
                            ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                            )}
                        </div>
                    );
                },
            },
            {
                id: "actions",
                header: "",
                enableSorting: false,
                cell: ({ row }) => {
                    const template = row.original;
                    return (
                        <ui.Popover>
                            <ui.PopoverTrigger asChild>
                                <ui.Button variant="ghost" size="icon">
                                    <MoreVertical size={20} />
                                </ui.Button>
                            </ui.PopoverTrigger>
                            <ui.PopoverContent align="end" className="p-1 w-44">
                                <div className="flex flex-col">
                                    <ui.Button
                                        variant="ghost"
                                        size="sm"
                                        className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                                        onClick={() => {
                                            if (template.settings) {
                                                navigate({
                                                    to: "/templates/$templateId/trackers",
                                                    params: {
                                                        templateId: template.template_id,
                                                    },
                                                    search: template.settings as object,
                                                });
                                            }
                                        }}
                                    >
                                        <ClipboardEdit size={16} />
                                        Edit Template
                                    </ui.Button>
                                    <ui.Button
                                        variant="ghost"
                                        size="sm"
                                        className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                                        onClick={() =>
                                            navigate({
                                                to: "/templates/$templateId/publish",
                                                params: {
                                                    templateId: template.template_id,
                                                },
                                            })
                                        }
                                    >
                                        <Globe size={16} />
                                        Publish
                                    </ui.Button>
                                    <ui.Button
                                        variant="ghost"
                                        size="sm"
                                        className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto"
                                        onClick={() => duplicateTemplate(template.template_id)}
                                    >
                                        <Copy size={16} />
                                        Duplicate
                                    </ui.Button>
                                    <ui.Button
                                        variant="ghost"
                                        size="sm"
                                        className="justify-start gap-2 px-2 py-1.5 pr-4 w-full h-auto text-red-600 hover:text-red-600"
                                        onClick={() => deleteTemplate(template.template_id)}
                                    >
                                        <Trash size={16} />
                                        Delete
                                    </ui.Button>
                                </div>
                            </ui.PopoverContent>
                        </ui.Popover>
                    );
                },
            },
        ],
        [navigate, duplicateTemplate, deleteTemplate, forms, surgeryOptions]
    );

    const table = useReactTable({
        data: templates ?? [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="font-semibold text-3xl tracking-tight">Templates</h1>
                    <p className="text-muted-foreground">
                        View and manage all templates - public templates are available to all clinicians
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <DraftTemplateWithAiButton />
                    <Button size="sm" asChild>
                        <Link to="/templates">
                            <Plus className="mr-2 w-4 h-4" />
                            Create Template
                        </Link>
                    </Button>
                </div>
            </div>

            <ui.Card>
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className={cn(
                                            header.column.getCanSort() &&
                                                "cursor-pointer select-none hover:bg-slate-50 dark:hover:bg-slate-800",
                                            (header.id === "specialties" || header.id === "forms") && "min-w-[240px]"
                                        )}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="py-12 text-muted-foreground text-center">
                                    <p className="mb-2">No templates found.</p>
                                    <Button variant="link" size="sm" asChild>
                                        <Link to="/templates">Create your first template</Link>
                                    </Button>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </ui.Card>
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/templates/")({
    component: TemplatesBuilder,
});
